import { Eye } from 'react-bootstrap-icons';
import {
  COMPLICATED_DEALS_OPTIONS,
  IRR_CALCULATOR_KEY,
  PAPER_CALCULATOR_KEY,
  PINUI_BINUI_CALCULATOR_KEY,
  REAL_ESTATE_ANALYST_ROLE,
  getProjectStageOptions
} from '../../helpers/constants';
import cities from '../../helpers/israel-cities.json';
import { snakeCase } from 'lodash';
import { GROUPPED_USER_BASED_ON_ROLE } from '../../helpers/overviewColumnsFields';

export const getInformationalFields = (type, addApartmentMode, users = [], loggedInUser, translate) => {
  const allInfoFields = [
    {
      label: 'area_in_square_meters',
      objectKey: 'areaInSquareMeters',
      defaultValueCompSymbol: 'A14',
      type: 'number'
    },
    {
      label: 'real_estate_analyst',
      objectKey: 'realEstateAnalyst',
      type: 'grouped-dropdown',
      required: true,
      options: GROUPPED_USER_BASED_ON_ROLE({
        users: users,
        priotize: REAL_ESTATE_ANALYST_ROLE,
        translate
      }),
      hide: !addApartmentMode
    },
    { label: 'city', objectKey: 'city', dataListOptions: cities.map(c => c.city), as: 'input', required: true },
    { label: 'address', objectKey: 'address', type: 'text' },
    { label: 'realtor_seller_name', objectKey: 'realtorSellerName' },
    { label: 'apartment_floor_number', objectKey: 'apartmentFloorNumber' },
    { label: 'number_of_rooms', objectKey: 'numberOfRooms' },

    { label: 'physical_condition', objectKey: 'physicalCondition', hide: type === PAPER_CALCULATOR_KEY },

    { label: 'developer_company', objectKey: 'developerCompany', hide: type === IRR_CALCULATOR_KEY },
    { label: 'signature_percentage', objectKey: 'signaturePercentage', hide: type !== PINUI_BINUI_CALCULATOR_KEY },
    { label: 'additional_comments', objectKey: 'additionalComments', type: 'linkifiedText' }
  ];

  if (type !== IRR_CALCULATOR_KEY) {
    allInfoFields.push(
      //profile related fields
      {
        label: 'required_cash_for_deal',
        objectKey: 'requiredCashForDeal',
        type: 'number',
        hide: type === PAPER_CALCULATOR_KEY
      },
      {
        label: 'developer_company_scale',
        objectKey: 'developerCompanyScale',
        type: 'range',
        min: 1,
        max: 10,
        hide: type === PINUI_BINUI_CALCULATOR_KEY
      },
      {
        label: 'developer_loan',
        objectKey: 'developerLoan',
        type: 'switch',
        trueValue: 'Yes',
        falseValue: 'No',
        hide: type === PINUI_BINUI_CALCULATOR_KEY
      },
      {
        label: 'linear_payment',
        objectKey: 'linearPayment',
        type: 'switch',
        trueValue: 'Yes',
        falseValue: 'No',
        hide: type === PINUI_BINUI_CALCULATOR_KEY
      },

      {
        label: 'buying_decision_scale',
        objectKey: 'buyingDecisionScale',
        type: 'switch',
        trueValue: 'Yes',
        falseValue: 'No',
        neutralValue: null
      },

      {
        label: 'large_scale_renovation',
        objectKey: 'largeRenovation',
        type: 'switch',
        trueValue: 'Yes',
        falseValue: 'No',
        neutralValue: null,
        hide: type === PAPER_CALCULATOR_KEY
      },
      {
        label: 'early_stage_deals',
        objectKey: 'earlyDeal',
        type: 'switch',
        trueValue: 'Yes',
        falseValue: 'No',
        hide: type === PAPER_CALCULATOR_KEY
      },
      {
        label: 'has_3_floor_or_higher_without_elevator',
        objectKey: 'noElevator',
        type: 'switch',
        trueValue: 'Yes',
        falseValue: 'No',
        hide: type === PAPER_CALCULATOR_KEY
      },
      {
        label: 'complicated_deals',
        objectKey: 'complicatedDeals',
        type: 'multi-checkbox',
        options: COMPLICATED_DEALS_OPTIONS.map(deal => ({ label: snakeCase(deal), value: deal })),
        hide: type === PAPER_CALCULATOR_KEY
      },
      {
        label: 'willingness_for_mixed_population',
        objectKey: 'mixedPopulation',
        type: 'switch',
        trueValue: 'Yes',
        falseValue: 'No'
      }
    );
  }

  return allInfoFields.filter(fi => !fi?.hide).map(f => ({ ...f, isInput: true, type: f.type || 'text' }));
};

export const getIrrFields = ({
  fieldsFromServer = [],
  lang,
  addApartmentMode = false,
  users = [],
  loggedInUser,
  translate
}) => {
  let fields = [
    { objectKey: 'askedPrice', isInput: true },
    { objectKey: 'inputPrice', isInput: true },
    { objectKey: 'appraisedValue', isInput: true },
    {
      objectKey: 'monthlyRent',
      isInput: true
    },
    { objectKey: 'rentIncrease', isInput: true, type: 'percentage' },
    { objectKey: 'maximalMortgageAvailable', isInput: true, hide: addApartmentMode },
    { objectKey: 'timeToDeliveryAfterBuyingInMonths', isInput: true, defaultValueCompSymbol: 'A15' },
    {
      objectKey: 'mortgagePercentage',
      isInput: true,
      defaultValueCompSymbol: 'A16',
      type: 'percentage',
      hide: addApartmentMode
    },
    { objectKey: 'mortgageToBeTakenDependingOnSeveralOptions', hide: addApartmentMode },
    {
      objectKey: 'mortgageInterest',
      isInput: true,
      type: 'percentage',
      defaultValueCompSymbol: 'A11',
      hide: addApartmentMode
    },
    {
      objectKey: 'mortgageTimeToMaturityInMonths',
      isInput: true,
      defaultValueCompSymbol: 'A17',
      hide: addApartmentMode
    },
    { objectKey: 'expectedAppreciation', isInput: true, type: 'percentage', defaultValueCompSymbol: 'A12' },
    {
      objectKey: 'realEstateHoldingPeriodInYears',
      isInput: true,
      type: 'monthYearInput',
      defaultValueCompSymbol: 'A18',
      hide: addApartmentMode
    },
    {
      objectKey: 'realtorCostsInPercentageTermsPreVat',
      isInput: true,
      type: 'percentage',
      defaultValueCompSymbol: 'A19'
    },
    { objectKey: 'renovationCost', isInput: true, defaultValueCompSymbol: 'A20' },
    { objectKey: 'valueAddedByRenovation', isInput: true },
    { objectKey: 'valueAfterRenovation' },
    { objectKey: 'relatedCostsLawyerAppraiserEtc', isInput: true, defaultValueCompSymbol: 'A21' },
    {
      objectKey: 'onlyApartmentWhileBuying',
      isInput: true,
      defaultValue: true,
      type: 'boolean',
      hide: addApartmentMode
    },
    {
      objectKey: 'onlyApartmentWhileSelling',
      isInput: true,
      defaultValue: true,
      type: 'boolean',
      hide: addApartmentMode
    },
    {
      objectKey: 'taxOnRent',
      isInput: true,
      defaultValueCompSymbol: 'A22',
      type: 'percentage',
      hide: addApartmentMode
    },
    { objectKey: 'pricePerSquareMeter' },
    { objectKey: 'buyingTax' },
    { objectKey: 'realtorCost' },
    { objectKey: 'differenceBetweenAppraisedValueAndAttractivePrice' },
    { objectKey: 'costOfTimeToDeliveryAfterBuying' },
    { objectKey: 'renovationCost' },
    { objectKey: 'equityForTakingMortgage' },
    { objectKey: 'costOfRealtorBuyerAndAllRelatedCosts' },
    { objectKey: 'totalEquityToPutInTheDeal' },
    { objectKey: 'totalDealCost' },
    { objectKey: 'mortgageToBeTakenDependingOnSeveralOptions' },
    { objectKey: 'mortgageMonthlyPayment' },
    { objectKey: 'totalMonthlyPayments' },
    { objectKey: 'mortgageLeftAtTheEndPeriodOfHolding' },
    { objectKey: 'totalLoanDecrease' },
    { objectKey: 'monthlyRent' },
    { objectKey: 'costOfVacancyAndAnnualRepairs' },
    { objectKey: 'yearlyReturnFromRent', type: 'percentage' },
    { objectKey: 'taxOnRent', type: 'percentage' },
    { objectKey: 'netYearlyReturnOnRent', type: 'percentage' },
    { objectKey: 'sellingPrice' },
    { objectKey: 'profitFromAppreciation' },
    { objectKey: 'taxInSelling' },
    { objectKey: 'totalNetProfit' },
    { objectKey: 'monthlyNetProfit' },
    { objectKey: 'totalRoi', type: 'percentage' },
    { objectKey: 'irr', type: 'percentage', extraInfo: { Icon: Eye, key: 'irrBreakdown', timeLabel: 'month' } },
    { objectKey: 'targetIrr', isInput: true, type: 'percentage' },
    { objectKey: 'targetPrice', rowClassName: 'bg-light-blue-2 text-white large' },
    { objectKey: 'cashflow' },
    ...getInformationalFields(IRR_CALCULATOR_KEY, addApartmentMode, users, loggedInUser, translate)
  ];

  const categoriesToHide = !addApartmentMode
    ? [
        CALCULATOR_FIELDS_CATEGORIES.PROFILE_FIELDS,
        CALCULATOR_FIELDS_CATEGORIES.APARTMENT_INFO,
        CALCULATOR_FIELDS_CATEGORIES.TARGET
      ]
    : [];
  return mapAndGroupFields({ fields, fieldsFromServer, lang, categoriesToHide });
};

export const getPinuiBinuiIrrFields = ({
  fieldsFromServer = [],
  lang,
  addApartmentMode = false,
  users = [],
  loggedInUser,
  translate
}) => {
  let fields = [
    { objectKey: 'askedPrice', isInput: true },
    { objectKey: 'inputPrice', isInput: true },
    { objectKey: 'appraisedValue', isInput: true },
    { objectKey: 'estimatedTimeForDemolition', isInput: true, type: 'monthYearInput' },
    {
      objectKey: 'estimatedTimeForFinishingContruction',
      isInput: true,
      defaultValueCompSymbol: 'A24',
      type: 'monthYearInput'
    },
    { objectKey: 'cashflow' },
    { objectKey: 'valueOfApartmentToday', isInput: true },
    { objectKey: 'monthlyRent', isInput: true },
    { objectKey: 'rentPostConstruction', isInput: true },
    {
      label: translate('project_stage'),
      objectKey: 'projectStage',
      type: 'dropdown',
      defaultValue: translate('select'),
      isInput: true,
      options: getProjectStageOptions(translate)
    },
    { objectKey: 'rentIncrease', isInput: true, type: 'percentage' },
    { objectKey: 'maximalMortgageAvailable', isInput: true, hide: addApartmentMode },
    { objectKey: 'apartmentSizeAfterReconstruction', isInput: true },
    { objectKey: 'apartmentSizeAfterReconstructionLikely', isInput: true },

    { objectKey: 'timeToDeliveryAfterBuyingInMonths', isInput: true, defaultValueCompSymbol: 'A15' },
    {
      objectKey: 'mortgagePercentage',
      isInput: true,
      defaultValueCompSymbol: 'A16',
      type: 'percentage',
      hide: addApartmentMode
    },
    { objectKey: 'mortgageToBeTakenDependingOnSeveralOptions', hide: addApartmentMode },
    {
      objectKey: 'mortgageInterest',
      isInput: true,
      type: 'percentage',
      defaultValueCompSymbol: 'A11',
      hide: addApartmentMode
    },
    {
      objectKey: 'mortgageTimeToMaturityInMonths',
      isInput: true,
      defaultValueCompSymbol: 'A17',
      hide: addApartmentMode
    },
    { objectKey: 'expectedAppreciation', isInput: true, type: 'percentage', defaultValueCompSymbol: 'A12' },
    {
      objectKey: 'realEstateHoldingPeriodInYears',
      isInput: true,
      defaultValueCompSymbol: 'A18',
      type: 'monthYearInput',
      hide: addApartmentMode
    },
    {
      objectKey: 'realtorCostsInPercentageTermsPreVat',
      isInput: true,
      type: 'percentage',
      defaultValueCompSymbol: 'A19'
    },
    { objectKey: 'renovationCost', isInput: true, defaultValueCompSymbol: 'A20' },
    { objectKey: 'valueAddedByRenovation', isInput: true },
    { objectKey: 'valueAfterRenovation' },
    { objectKey: 'relatedCostsLawyerAppraiserEtc', isInput: true, defaultValueCompSymbol: 'A21' },
    {
      objectKey: 'onlyApartmentWhileBuying',
      isInput: true,
      defaultValue: true,
      type: 'boolean',
      hide: addApartmentMode
    },
    {
      objectKey: 'onlyApartmentWhileSelling',
      isInput: true,
      defaultValue: true,
      type: 'boolean',
      hide: addApartmentMode
    },
    {
      objectKey: 'taxOnRent',
      isInput: true,
      defaultValueCompSymbol: 'A22',
      type: 'percentage',
      hide: addApartmentMode
    },
    { objectKey: 'pricePerSquareMeter' },
    { objectKey: 'buyingTax' },
    { objectKey: 'realtorCost' },
    { objectKey: 'differenceBetweenAppraisedValueAndAttractivePrice' },
    { objectKey: 'costOfTimeToDeliveryAfterBuying' },
    { objectKey: 'equityForTakingMortgage' },
    { objectKey: 'costOfRealtorBuyerAndAllRelatedCosts' },
    { objectKey: 'totalEquityToPutInTheDeal' },
    { objectKey: 'totalDealCost' },
    { objectKey: 'mortgageMonthlyPayment' },
    { objectKey: 'totalMonthlyPayments' },
    { objectKey: 'mortgageLeftAtTheEndPeriodOfHolding' },
    { objectKey: 'totalLoanDecrease' },
    { objectKey: 'costOfVacancyAndAnnualRepairs' },
    { objectKey: 'yearlyReturnFromRent', type: 'percentage' },
    { objectKey: 'netYearlyReturnOnRent', type: 'percentage' },
    { objectKey: 'sellingPrice' },
    { objectKey: 'profitFromAppreciation' },
    { objectKey: 'taxInSelling' },
    { objectKey: 'totalNetProfit' },
    { objectKey: 'monthlyNetProfit' },
    { objectKey: 'totalRoi', type: 'percentage' },
    { objectKey: 'irr', type: 'percentage', extraInfo: { Icon: Eye, key: 'irrBreakdown', timeLabel: 'month' } },
    {
      objectKey: 'irrInNoReconstruction',
      type: 'percentage',
      extraInfo: { Icon: Eye, key: 'irrNoReconstructionBreakdown', timeLabel: 'month' }
    },
    { objectKey: 'targetIrr', isInput: true, type: 'percentage' },
    { objectKey: 'targetPrice', rowClassName: 'bg-light-blue-2 text-white large' },
    ...getInformationalFields(PINUI_BINUI_CALCULATOR_KEY, addApartmentMode, users, loggedInUser, translate)
  ];

  const categoriesToHide = !addApartmentMode
    ? [
        CALCULATOR_FIELDS_CATEGORIES.PROFILE_FIELDS,
        CALCULATOR_FIELDS_CATEGORIES.APARTMENT_INFO,
        CALCULATOR_FIELDS_CATEGORIES.TARGET
      ]
    : [];
  return mapAndGroupFields({ fields, fieldsFromServer, lang, categoriesToHide });
};

export const getPaperApartmentIrrFields = ({
  fieldsFromServer = [],
  lang,
  addApartmentMode = false,
  users = [],
  loggedInUser,
  translate
}) => {
  let fields = [
    { objectKey: 'askedPrice', isInput: true },
    { objectKey: 'inputPrice', isInput: true },
    {
      objectKey: 'remainingPayment',
      isInput: true,
      type: 'dropdown',
      defaultValue: 'oneFinalPayment',
      options: [{ value: 'oneFinalPayment' }, { value: 'gradualPayment' }]
    },
    { objectKey: 'cashflow' },
    { objectKey: 'paperApartmentPayments', isInput: true, type: 'paperApartmentPayments', defaultValue: [] },
    { objectKey: 'yearlyMaterialCostIndexRise', isInput: true, type: 'percentage' },
    {
      objectKey: 'estimatedTimeForFinishingContruction',
      isInput: true,
      type: 'monthYearInput',
      defaultValueCompSymbol: 'A24'
    },
    { objectKey: 'additionalCostsForMakingApartmentReadyForRent', isInput: true },
    { objectKey: 'valueOfApartmentToday', isInput: true },
    { objectKey: 'monthlyRent', isInput: true },
    { objectKey: 'rentIncrease', isInput: true, type: 'percentage' },
    { objectKey: 'maximalMortgageAvailable', isInput: true, hide: addApartmentMode },
    {
      objectKey: 'mortgagePercentage',
      isInput: true,
      defaultValueCompSymbol: 'A16',
      type: 'percentage',
      hide: addApartmentMode
    },
    { objectKey: 'mortgageToBeTakenDependingOnSeveralOptions', hide: addApartmentMode },
    {
      objectKey: 'mortgageInterest',
      isInput: true,
      type: 'percentage',
      defaultValueCompSymbol: 'A11',
      hide: addApartmentMode
    },
    {
      objectKey: 'mortgageTimeToMaturityInMonths',
      isInput: true,
      defaultValueCompSymbol: 'A17',
      hide: addApartmentMode
    },
    { objectKey: 'expectedAppreciation', isInput: true, type: 'percentage', defaultValueCompSymbol: 'A12' },
    {
      objectKey: 'realEstateHoldingPeriodInYears',
      isInput: true,
      type: 'monthYearInput',
      defaultValueCompSymbol: 'A18'
    },
    {
      objectKey: 'realtorCostsInPercentageTermsPreVat',
      isInput: true,
      type: 'percentage',
      defaultValueCompSymbol: 'A19'
    },
    { objectKey: 'relatedCostsLawyerAppraiserEtc', isInput: true, defaultValueCompSymbol: 'A21' },
    {
      objectKey: 'onlyApartmentWhileBuying',
      isInput: true,
      defaultValue: true,
      type: 'boolean',
      hide: addApartmentMode
    },
    {
      objectKey: 'onlyApartmentWhileSelling',
      isInput: true,
      defaultValue: true,
      type: 'boolean',
      hide: addApartmentMode
    },
    {
      objectKey: 'taxOnRent',
      isInput: true,
      defaultValueCompSymbol: 'A22',
      type: 'percentage',
      hide: addApartmentMode
    },
    { objectKey: 'pricePerSquareMeter' },
    { objectKey: 'buyingTax' },
    { objectKey: 'realtorCost' },
    { objectKey: 'equityForTakingMortgage' },
    { objectKey: 'costOfRealtorBuyerAndAllRelatedCosts' },
    { objectKey: 'totalEquityToPutInTheDeal' },
    { objectKey: 'totalDealCost' },
    { objectKey: 'stillInsufficientAmountForBuying' },
    { objectKey: 'mortgageMonthlyPayment' },
    { objectKey: 'totalMonthlyPayments' },
    { objectKey: 'mortgageLeftAtTheEndPeriodOfHolding' },
    { objectKey: 'totalLoanDecrease' },
    { objectKey: 'costOfVacancyAndAnnualRepairs' },
    { objectKey: 'taxOnRent', type: 'percentage' },
    { objectKey: 'sellingPrice' },
    { objectKey: 'profitFromAppreciation' },
    { objectKey: 'taxInSelling' },
    { objectKey: 'totalNetProfit' },
    { objectKey: 'monthlyNetProfit' },
    { objectKey: 'totalRoi', type: 'percentage' },
    { objectKey: 'irr', type: 'percentage', extraInfo: { Icon: Eye, key: 'irrBreakdown', timeLabel: 'month' } },
    { objectKey: 'targetIrr', isInput: true, type: 'percentage' },
    { objectKey: 'targetPrice', rowClassName: 'bg-light-blue-2 text-white large' },
    ...getInformationalFields(PAPER_CALCULATOR_KEY, addApartmentMode, users, loggedInUser, translate)
  ];

  const categoriesToHide = !addApartmentMode
    ? [
        CALCULATOR_FIELDS_CATEGORIES.PROFILE_FIELDS,
        CALCULATOR_FIELDS_CATEGORIES.APARTMENT_INFO,
        CALCULATOR_FIELDS_CATEGORIES.TARGET
      ]
    : [];
  return mapAndGroupFields({ fields, fieldsFromServer, lang, categoriesToHide });
};

const mapAndGroupFields = ({ fields = [], fieldsFromServer = [], lang, categoriesToHide = [] }) => {
  const fieldsToSearch = [...fieldsFromServer];
  const allTranslatedFields = fields
    .map(field => {
      const matchIndex = fieldsToSearch.findIndex(f => f.key === field.objectKey);
      const match = matchIndex !== -1 ? fieldsToSearch.splice(matchIndex, 1)[0] : undefined;
      return {
        ...field,
        label: match?.[lang] || field.objectKey,
        index: match?.index || -1,
        category: match?.category || 'others',
        description: match?.description || ''
      };
    })
    .sort((a, b) => a.index - b.index);

  const groupedFields = [...ALL_CALCULATOR_FIELD_CATEGORIES, 'others']
    .filter(category => !categoriesToHide.includes(category))
    .reduce((acc, category) => {
      const allFields = allTranslatedFields.filter(f => f.category === category);
      if (allFields.length > 0) {
        acc[category] = allFields;
      }
      return acc;
    }, {});

  return groupedFields;
};

//fields from server is array of objects which has translations and index
export const getAllRSUFields = (fieldsFromServer = [], lang) => {
  const allFields = [
    {
      objectKey: 'MarginalRateOfIncome',
      isInput: true,
      defaultValueCompSymbol: 'A26',
      type: 'percentage',
      linkedWithFirstSection: true
    },
    {
      objectKey: 'CapitalAppreciationTax',
      isInput: true,
      defaultValueCompSymbol: 'A27',
      type: 'percentage',
      linkedWithFirstSection: true
    },
    {
      objectKey: 'ValueWithMarginalTaxRate',
      isInput: true,
      linkedWithFirstSection: true
    },
    {
      objectKey: 'ValueWithCapitalAppreciationTaxRate',
      isInput: true,
      linkedWithFirstSection: true
    },
    {
      objectKey: 'TotalValue'
    },
    {
      objectKey: 'YearlyAppreciation',
      isInput: true,
      type: 'percentage'
    },
    {
      objectKey: 'HoldingPeriodInYears',
      isInput: true,
      linkedWithFirstSection: true
    },
    {
      objectKey: 'PayingTaxAtPeriodEnd',
      isInput: true,
      type: 'boolean',
      defaultValue: true
    },
    {
      objectKey: 'FutureValuePreTax'
    },
    {
      objectKey: 'Tax'
    },
    {
      objectKey: 'NetValue'
    }
  ]
    .map(field => {
      const match = fieldsFromServer.find(f => f.key === field.objectKey);
      return {
        ...field,
        label: match?.[lang],
        index: match?.index
      };
    })
    .sort((a, b) => a.index - b.index);

  return allFields;
};

export const getAllOptionsFields = (fieldsFromServer = [], translate, lang) => {
  const allFields = [
    {
      objectKey: 'CompanyType',
      isInput: true,
      type: 'dropdown',
      required: true,
      linkedWithFirstSection: true,
      defaultValue: 'publicCompany',
      options: [
        { label: translate('public_company'), value: 'publicCompany' },
        { label: translate('private_company'), value: 'privateCompany' }
      ]
    },
    {
      objectKey: 'MarginalRateOfIncome',
      isInput: true,
      defaultValueCompSymbol: 'A26',
      type: 'percentage',
      linkedWithFirstSection: true
    },
    {
      objectKey: 'CapitalAppreciationTax',
      isInput: true,
      defaultValueCompSymbol: 'A27',
      type: 'percentage',
      linkedWithFirstSection: true
    },
    {
      objectKey: 'OptionsStrikePrice',
      isInput: true,
      linkedWithFirstSection: true
    },
    {
      objectKey: 'StockValueAtOptionsReceivalDate',
      isInput: true,
      linkedWithFirstSection: true
    },
    {
      objectKey: 'CurrentStockPrice',
      isInput: true,
      linkedWithFirstSection: true
    },
    {
      objectKey: 'StockAmount',
      isInput: true,
      linkedWithFirstSection: true
    },
    //
    {
      objectKey: 'RequiredEquityForExercising'
    },
    {
      objectKey: 'ProfitAtExcersisingPreTax'
    },
    {
      objectKey: 'TaxOnExercising'
    },
    {
      objectKey: 'NetAfterExercisingAndSelling'
    }
  ]
    .map(field => {
      const match = fieldsFromServer.find(f => f.key === field.objectKey);
      return {
        ...field,
        label: match?.[lang],
        index: match?.index
      };
    })
    .sort((a, b) => a.index - b.index);

  return allFields;
};

export const CALCULATOR_FIELDS_CATEGORIES = {
  GENERAL_INFORMATION: 'General Information',
  ADDITIONAL_INFORMATION: 'Additional Information',
  BUYING: 'Buying',
  MORTGAGE: 'Mortgage',
  RENT: 'Rent',
  SELLING: 'Selling',
  PROFIT: 'Profit',
  TARGET: 'Target',
  APARTMENT_INFO: 'Apartment Info',
  PROFILE_FIELDS: 'Profile fields'
};

export const ALL_CALCULATOR_FIELD_CATEGORIES = Object.values(CALCULATOR_FIELDS_CATEGORIES);
